import React from 'react';
import { Link } from 'react-router-dom';
import { DependencyNodeId } from 'sched';
import { useDatabase } from '../../hooks/useDatabase';
import { dependencyNodeIdToString } from '../graph/DependencyGraph';
import { FiCheckSquare, FiClock, FiHelpCircle, FiPlayCircle } from 'react-icons/fi';

interface DependencyNodesTableProps {
  nodeIds: DependencyNodeId[];
  emptyMessage?: string;
  onRemove?: (id: DependencyNodeId) => void;
  // Optional: via_choice info for Decision nodes - TODO: plumb this through from dependency graph
  viaChoices?: Record<string, string | undefined>;
}

const DependencyNodesTable: React.FC<DependencyNodesTableProps> = ({
  nodeIds,
  emptyMessage = 'No items found',
  onRemove,
  viaChoices = {},
}) => {
  const { database, workspaceId } = useDatabase();

  if (!nodeIds.length) {
    return <p className="text-cm-text-muted text-sm italic py-2">{emptyMessage}</p>;
  }

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm text-left text-cm-text-muted">
        <thead className="text-xs text-cm-text-primary uppercase bg-cm-bg-elevated">
          <tr>
            <th scope="col" className="px-6 py-3">Type</th>
            <th scope="col" className="px-6 py-3">Title</th>
            <th scope="col" className="px-6 py-3">Status</th>
            {onRemove && <th scope="col" className="px-6 py-3 text-right">Actions</th>}
          </tr>
        </thead>
        <tbody>
          {nodeIds.map((nodeId) => {
            // Extract type and ID
            let type = 'Unknown';
            let id = '';
            let title = '';
            let status;
            let linkPath = '';

            // Type checks using 'in' operator - each check narrows the type
            const isTask = 'Task' in nodeId;
            const isOutcome = !isTask && 'Outcome' in nodeId;
            const isDecision = !isTask && !isOutcome && 'Decision' in nodeId;
            const isWorkflowInstance = !isTask && !isOutcome && !isDecision && 'WorkflowInstance' in nodeId;

            if (isTask) {
              type = 'Task';
              id = nodeId.Task;
              linkPath = `/workspaces/${workspaceId}/tasks/${id}`;

              // Get task details from database
              if (database) {
                const tasks = database.tasks();
                const task = tasks[id];
                title = task?.title() ?? '';
                status = task?.complete() ? <FiCheckSquare /> : <FiClock />;
              }
            } else if (isOutcome) {
              type = 'Outcome';
              id = nodeId.Outcome;
              linkPath = `/workspaces/${workspaceId}/outcomes/${id}`;

              // Get outcome details from database
              if (database) {
                const outcomes = database.outcomes();
                const outcome = outcomes[id];
                title = outcome?.title() ?? '';
                // Outcomes don't have a direct "complete" property
                // TODO use the database cache for this, that stores completions
                status = <span></span>;
              }
            } else if (isDecision) {
              type = 'Decision';
              id = nodeId.Decision;
              linkPath = `/workspaces/${workspaceId}/decisions/${id}`;

              // Get decision details from database
              if (database) {
                const decisions = database.decisions();
                const decision = decisions[id];
                title = decision?.title() ?? '';
                const viaChoice = viaChoices[id];
                if (viaChoice) {
                  title += ` (via: ${viaChoice})`;
                }
                status = <FiHelpCircle />;
              }
            } else if (isWorkflowInstance) {
              type = 'Workflow';
              id = (nodeId as { WorkflowInstance: string }).WorkflowInstance;
              linkPath = `/workspaces/${workspaceId}/workflow-instances/${id}`;

              // Get workflow instance details from database
              if (database) {
                const instances = database.workflow_instances();
                const instance = instances[id];
                // Show template ID as title (or could fetch template title via separate lookup)
                title = instance?.template_id() ?? '';
                status = <FiPlayCircle />;
              }
            }

            const nodeKey = dependencyNodeIdToString(nodeId);

            return (
              <tr key={nodeKey} className="bg-cm-bg-surface border-b hover:bg-cm-bg-elevated">
                <td className="px-6 py-4">
                  {status}
                </td>
                <td className="px-6 py-4 whitespace-nowrap font-medium text-cm-text-primary">
                  {type}
                </td>
                <td className="px-6 py-4">
                  <Link
                    to={linkPath}
                    className="font-medium text-blue-600 hover:underline"
                  >
                    {title || `${type} ${id}`}
                  </Link>
                </td>
                {onRemove && (
                  <td className="px-6 py-4 text-right">
                    <button
                      type="button"
                      className="inline-flex items-center px-2 py-1 text-xs rounded hover:bg-red-50 dark:hover:bg-red-900/30 text-red-600"
                      onClick={() => onRemove(nodeId)}
                      title="Remove"
                    >
                      ✕
                    </button>
                  </td>
                )}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

export default DependencyNodesTable;
